import { ContextMenu } from 'devextreme-react';
import moment from 'moment';
import { isFunction } from 'ramda-extension';
import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '../../contexts/translation';
import { phoneCall, timespan } from '../../utils/customRenderer';
import { getWidthByColumns } from '../../utils/datagridNoResponsive';
import {
  displayDateFormat_CZ,
  displayDateFormat_EN,
  displayDateTimeFormat_CZ,
  displayDateTimeFormat_EN,
} from '../../utils/dates.types';
import { evaluateStringCondition } from '../../utils/generalReportHighlightFunctions';
import { DatagridNoResponsive } from '../datagridNoResponsive/DatagridNoResponsive';
import { SisDataGrid } from '../table';
import { IDataGrid } from './AssetsDataGrid';

interface Props extends IDataGrid {
  userCanCreate?: boolean;
  filterVisibility: boolean;
  setFilterVisibility: (e: boolean) => void;
  transFormName: string;
  newEntryActionPath: string;
  exportName?: string;
  dataAndStructure: any;
  selectedFilterDatagrid: any;
  datagridNoResponsiveClassName?: string;
  disableCustomToolbar?: boolean;
  disableCustomHeight?: boolean;
  enableGrouping?: boolean;
  enableXlsxExport?: boolean;
  enableColumnChooser?: boolean;
  enableHeaderFiltering?: boolean;
  enableRowFiltering?: boolean;
  enableSearchPanel?: boolean;
  enableColumnResizing?: boolean;
  enableColumnReordering?: boolean;
  disableDatagridNoResponsive?: boolean;
  enableHorizontalScrooling?: boolean;
  rowAlternationEnabled?: boolean;
  enableResetFilter?: boolean;
  enableFilterToggle?: boolean;
  datagridId: string;
  preserveDatagridSession?: boolean;
}

export const GeneralReportDataGrid = ({
  dataAndStructure,
  handleEdit,
  dataGridRef,
  userCanCreate,
  filterVisibility,
  setFilterVisibility,
  transFormName,
  newEntryActionPath,
  exportName,
  selectedFilterDatagrid,
  datagridNoResponsiveClassName,
  disableCustomToolbar,
  disableCustomHeight,
  enableGrouping = true,
  enableXlsxExport = true,
  enableColumnChooser = true,
  enableHeaderFiltering = true,
  enableRowFiltering = true,
  enableSearchPanel = true,
  enableColumnResizing = true,
  enableColumnReordering = true,
  enableResetFilter = true,
  enableFilterToggle = true,
  rowAlternationEnabled = true,
  disableDatagridNoResponsive,
  enableHorizontalScrooling,
  datagridId,
  preserveDatagridSession = false,
}: Props) => {
  const { translate, lang } = useTranslation();
  const transFormReportBar = 'Reports.Bar';
  const history = useHistory();
  const { pathname } = useLocation();

  const hasFilterRef = useRef(false);
  const toolbarBaseItemsRef = useRef<any[] | null>(null);

  const dateFormat = lang === 'en-US' ? displayDateFormat_EN : displayDateFormat_CZ;
  const dateTimeFormat = lang === 'en-US' ? displayDateTimeFormat_EN : displayDateTimeFormat_CZ;

  const onToolbarPreparing = (e) => {
    if (enableFilterToggle)
      e.toolbarOptions.items.unshift({
        location: 'after',
        widget: 'dxButton',
        options: {
          visible: true,
          text: translate!(filterVisibility ? 'Hide' : 'Show', transFormReportBar),
          type: 'normal',
          onClick: () => setFilterVisibility(!filterVisibility),
        },
      });

    if (dataAndStructure?.datagrid?.toolbar) {
      [...dataAndStructure?.datagrid?.toolbar].reverse().forEach((item) => {
        e.toolbarOptions.items.unshift({
          location: 'after',
          widget: 'dxButton',
          options: {
            ...item,
            onClick: () => {
              history.push(item.location);
            }
          }

        });
      });
    }

    e.toolbarOptions.items.unshift({
      name: "activeFilterTitle",
      cssClass: "title",
      template: () => `<span class="datagrid-active-filter">${translate!('You have active filter', transFormReportBar)}</span>`,
      visible: false,
    });

    if (enableResetFilter)
      e.toolbarOptions.items.push({
        location: 'after',
        widget: 'dxButton',
        options: {
          icon: 'clear',
          type: 'info',
          text: translate!('Clear filter', transFormName),
          stylingMode: 'outlined',
          onClick: () => {
            if (dataGridRef) {
              // @ts-ignore
              dataGridRef.current.instance.clearFilter();
            }
          },
        },
      });

    toolbarBaseItemsRef.current = [...(e.toolbarOptions.items || [])];
  };

  const dataGridHeight = Math.ceil(window.outerHeight * 0.7);

  // Doplní proměnné v textu
  const transformParams = (text, cellData) => {
    const params = [...text.matchAll('{(.*?)}')];
    params?.forEach((param) => (text = text.replace(`${param[0]}`, cellData.data[param[1]])));
    return text;
  };

  const processLinkAttribute = (item) => {
    return {
      ...item,
      cellRender: (cellData) => {
        let location: string = transformParams(item.link.location, cellData);
        let title: string = transformParams(item.link.title, cellData);
        return (
          <>
            <span
              style={{ textDecoration: 'underline', cursor: 'pointer' }}
              className={`context-link-${cellData.data.id}-${item.link.id}`}
              // onClick nenaslouchá na pravé tlačítko myši
              onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
                // stisknuto levé tlačítko
                if (e.nativeEvent.button === 0) {
                  sessionStorage.setItem(`${datagridId}_navigated_away`, '1');
                  return history.push({
                    pathname: location,
                    state: { backRoute: pathname, defaultTab: item.link.defaultTab },
                  });
                }
              }}
              title={title}
            >
              {cellData.data[cellData.column.dataField]}
            </span>
            <ContextMenu
              dataSource={[
                {
                  text: translate!('Open link in new tab', ''),
                  icon: 'dx-icon-globe',
                },
              ]}
              target={`.context-link-${cellData.data.id}-${item.link.id}`}
              onItemClick={() => {
                if (location.includes('http')) {
                  // externí link
                  window.open(location, '_blank');
                } else {
                  // interní link
                  window.open('#' + location, '_blank');
                }
              }}
            />
          </>
        );
      },
    };
  };

  const processHighlightConditionsAttribute = (item) => {
    return {
      ...item,
      cellRender: (cellData) => {
        let toReturn: any;
        if (cellData.column.dataType === 'date')
          toReturn = moment(cellData.data[cellData.column.dataField]).format(dateFormat);
        else if (cellData.column.dataType === 'datetime')
          toReturn = moment(cellData.data[cellData.column.dataField]).format(dateTimeFormat);
        else if (item.customRender === 'Timespan')
          toReturn = timespan(cellData.data[cellData.column.dataField]);
        else if (item.customRender === 'PhoneCall')
          toReturn = phoneCall(cellData.data[cellData.column.dataField]);
        else toReturn = cellData.data[cellData.column.dataField];

        // Může existovat n podmínek, vyhodnocují se postupně, pokud jedna platí, cyklus končí
        item.highlightConditions.every((cond) => {
          if (evaluateStringCondition(transformParams(cond.condition, cellData), cond.dataType)) {
            let style: CSSProperties = {};
            if (cond.color) style = { ...style, color: cond.color };
            if (cond.backgroundColor)
              style = { ...style, padding: '2px 5px', backgroundColor: cond.backgroundColor };
            if (cond.textDecoration) style = { ...style, textDecoration: cond.textDecoration };
            if (cond.fontWeight) style = { ...style, fontWeight: cond.fontWeight };

            toReturn = <span style={style}>{toReturn}</span>;
            return false;
          } else {
            if (cellData.column.dataType === 'date')
              toReturn = moment(cellData.data[cellData.column.dataField]).format(dateFormat);
            else if (cellData.column.dataType === 'datetime')
              toReturn = moment(cellData.data[cellData.column.dataField]).format(dateTimeFormat);
            else if (item.customRender === 'Timespan')
              toReturn = timespan(cellData.data[cellData.column.dataField]);
            else if (item.customRender === 'PhoneCall')
              toReturn = phoneCall(cellData.data[cellData.column.dataField]);
            else toReturn = cellData.data[cellData.column.dataField];
            return true;
          }
        });
        return toReturn;
      },
    };
  };

  const processHighlightConditionsAttributeWithLink = (item) => {
    return {
      ...item,
      cellRender: (cellData) => {
        let location: string = transformParams(item.link.location, cellData);
        let title: string = transformParams(item.link.title, cellData);

        let toReturn: any;
        if (cellData.column.dataType === 'date')
          toReturn = moment(cellData.data[cellData.column.dataField]).format(dateFormat);
        else if (cellData.column.dataType === 'datetime')
          toReturn = moment(cellData.data[cellData.column.dataField]).format(dateTimeFormat);
        else if (item.customRender === 'Timespan')
          toReturn = timespan(cellData.data[cellData.column.dataField]);
        else if (item.customRender === 'PhoneCall')
          toReturn = phoneCall(cellData.data[cellData.column.dataField]);
        else toReturn = cellData.data[cellData.column.dataField];

        // Může existovat n podmínek, vyhodnocují se postupně, pokud jedna platí, cyklus končí
        item.highlightConditions.every((cond) => {
          if (evaluateStringCondition(transformParams(cond.condition, cellData), cond.dataType)) {
            let style: CSSProperties = {};
            if (cond.color) style = { ...style, color: cond.color };
            if (cond.backgroundColor)
              style = { ...style, padding: '2px 5px', backgroundColor: cond.backgroundColor };
            if (cond.textDecoration) style = { ...style, textDecoration: cond.textDecoration };
            if (cond.fontWeight) style = { ...style, fontWeight: cond.fontWeight };

            toReturn =
              <>
                <span
                  style={{ ...style, textDecoration: 'underline', cursor: 'pointer' }}
                  className={`context-link-${cellData.data.id}-${item.link.id}`}
                  // onClick nenaslouchá na pravé tlačítko myši
                  onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
                    // stisknuto levé tlačítko
                    if (e.nativeEvent.button === 0) {
                      sessionStorage.setItem(`${datagridId}_navigated_away`, '1');
                      return history.push({
                        pathname: location,
                        state: { backRoute: pathname, defaultTab: item.link.defaultTab },
                      });
                    }
                  }}
                  title={title}
                >
                  {toReturn}
                </span>
                <ContextMenu
                  dataSource={[
                    {
                      text: translate!('Open link in new tab', ''),
                      icon: 'dx-icon-globe',
                    },
                  ]}
                  target={`.context-link-${cellData.data.id}-${item.link.id}`}
                  onItemClick={() => {
                    if (location.includes('http')) {
                      // externí link
                      window.open(location, '_blank');
                    } else {
                      // interní link
                      window.open('#' + location, '_blank');
                    }
                  }}
                />
              </>;
            return false;
          } else {
            if (cellData.column.dataType === 'date')
              toReturn = moment(cellData.data[cellData.column.dataField]).format(dateFormat);
            else if (cellData.column.dataType === 'datetime')
              toReturn = moment(cellData.data[cellData.column.dataField]).format(dateTimeFormat);
            else if (item.customRender === 'Timespan')
              toReturn = timespan(cellData.data[cellData.column.dataField]);
            else if (item.customRender === 'PhoneCall')
              toReturn = phoneCall(cellData.data[cellData.column.dataField]);
            else toReturn = cellData.data[cellData.column.dataField];
            return true;
          }
        });
        return toReturn;
      },
    };
  };

  const processHeaderStyleAttribute = (item) => {
    return {
      ...item,
      headerCellRender: (data) => {
        let style = {
          color: item.headerStyle?.color || '#8899a8',
          fontWeight: item.headerStyle?.fontWeight || 400,
          fontSize: item.headerStyle?.fontSize || 12,
          textAlign: item.headerStyle?.textAlign || 'right !important',
        };
        return <div style={style}>{data.column.caption}</div>;
      },
    };
  };

  const prepareColumns = () => {
    let columns = dataAndStructure?.datagrid.columns ?? [];
    columns.sort((col1, col2) => col1.visibleIndex - col2.visibleIndex);
    columns = columns.filter((col) => col.visible);
    columns = columns.map((item) => {
      let _item = item;
      if (_item.link) _item = processLinkAttribute(_item);
      if (_item.highlightConditions && _item.highlightConditions.length > 0) {
        _item = processHighlightConditionsAttribute(_item);
      }
      if (_item.link && _item.highlightConditions && _item.highlightConditions.length > 0) {
        _item = processHighlightConditionsAttributeWithLink(_item);
      }
      if (_item.headerStyle) {
        _item = processHeaderStyleAttribute(_item);
      }
      return _item;
    });

    return columns;
  };

  const columns = prepareColumns();

  const onCellPrepared = (e: any) => {
    if (!dataAndStructure || !dataAndStructure.datagrid || !dataAndStructure.datagrid.columns) return;

    if (e.rowType === 'header') {
      const columnData = dataAndStructure.datagrid.columns.find(x => x.dataField === e.column.dataField);
      if (columnData && columnData.backgroundColor)
        e.cellElement.style.backgroundColor = columnData.backgroundColor;
      if (columnData && columnData.color)
        e.cellElement.style.color = columnData.color;

    }

    if (e.rowType === 'data') {
      const columnData = dataAndStructure.datagrid.columns.find(x => x.dataField === e.column.dataField);
      if (columnData && !columnData.highlightConditions) return;

      columnData.highlightConditions.every((cond) => {
        if (evaluateStringCondition(transformParams(cond.condition, e), cond.dataType)) {
          if (cond.backgroundColorCell)
            e.cellElement.style.setProperty('background-color', cond.backgroundColorCell, 'important');

        }
      });
    }
  }

  useEffect(() => {
    if (selectedFilterDatagrid && !preserveDatagridSession)
      window.sessionStorage.setItem(datagridId, JSON.stringify(selectedFilterDatagrid));
  }, [dataGridRef, selectedFilterDatagrid]);

  const updateToolbarBadge = (grid: any, hasFilter: boolean) => {
    const base = toolbarBaseItemsRef.current;
    const current = grid.option("toolbar.items") || [];
    const baseline = base ?? current;
    const idx = baseline.findIndex((it: any) => it?.name === "activeFilterTitle");
    if (idx === -1) return;

    grid.option(`toolbar.items[${idx}].visible`, hasFilter);
  };

  const handleOptionChanged = (e: any) => {
    const grid = e.component;

    setTimeout(() => {
      const combined = grid.getCombinedFilter();
      const hasFilterNow = !!combined;

      if (hasFilterRef.current !== hasFilterNow) {
        hasFilterRef.current = hasFilterNow;
        updateToolbarBadge(grid, hasFilterNow);
      }
    }, 200);
  };

  const Datagrid = () => {
    return (
      <>
        <SisDataGrid
          rowAlternationEnabled={rowAlternationEnabled}
          customDataGridStyle={{ visibility: dataAndStructure ? 'visible' : 'hidden' }}
          enableXlsxExport={enableXlsxExport}
          exportName={exportName ?? 'export-data'}
          dataGridRef={dataGridRef}
          data={dataAndStructure?.data ?? []}
          keyExpr={'id'}
          height={disableCustomHeight ? 'auto' : dataGridHeight}
          virtualizationEnabled
          transFormName={transFormName}
          onToolbarPreparing={!disableCustomToolbar ? onToolbarPreparing : undefined}
          columns={columns}
          pageSizeChangeVisible={true}
          enableGrouping={enableGrouping}
          datagridConfig={dataAndStructure?.datagrid}
          generalReport
          enableHorizontalScrooling={enableHorizontalScrooling}
          columnChooser={enableColumnChooser}
          allowHeaderFiltering={enableHeaderFiltering}
          search={enableSearchPanel}
          allowColumnResizing={enableColumnResizing}
          allowColumnReordering={enableColumnReordering}
          filterRow={enableRowFiltering}
          wordWrapEnabled={
            dataAndStructure?.datagrid?.wordWrapEnabled
              ? dataAndStructure?.datagrid?.wordWrapEnabled
              : false
          }
          stateStoringKey={datagridId}
          totalSummaryEnabled={!!dataAndStructure?.datagrid.summary}
          totalSummaryColumns={dataAndStructure?.datagrid.summary}
          onCellPrepared={onCellPrepared}
          onOptionChanged={handleOptionChanged}
        />
      </>
    );
  };

  if (disableDatagridNoResponsive) return <Datagrid />;

  return (
    <DatagridNoResponsive
      datagridWidth={getWidthByColumns(columns, null, true)}
      className={datagridNoResponsiveClassName}
    >
      <Datagrid />
    </DatagridNoResponsive>
  );
};
